import { useState, useMemo, useEffect, useRef } from "react"
import {
	Stack,
	Text,
	Image,
	Box,
	Flex,
	MantineStyleProps,
	SimpleGrid,
	Center,
	Anchor
} from "@mantine/core"
import styles from "./PictureSelector.module.css"
import { useIsMobile } from "@utils/device.utils"
import {
	AEM_DICTIONARY_COLLECTIONS,
	useAEMDictionary
} from "@AEM/hooks/useAEMDictionary"
import { useAEMAvatars } from "@AEM/hooks/useAEMAvatars"
import {
	createInitialsAvatar,
	createInitialsPreview
} from "./CreateInitialsAvatar"
import { useAEMIcons } from "@AEM/hooks/useAEMIcons"
import { AEMSvg } from "@components/AEMSvg"

interface PictureSelectorProps extends MantineStyleProps {
	selectedAvatarId?: string
	onSelect?: (selection: { url: string; file: File }) => void
	initials?: string
	defaultImageUrl?: string
}

export const PictureSelector = ({
	selectedAvatarId = "",
	onSelect,
	initials = "",
	defaultImageUrl,
	...props
}: PictureSelectorProps) => {
	const { dictionary } = useAEMDictionary(
		AEM_DICTIONARY_COLLECTIONS.REGISTRATION
	)
	const { icons } = useAEMIcons()
	const photoIcon = icons?.["digital-asset-library.svg"]
	const { avatars } = useAEMAvatars()
	const [selected, setSelected] = useState<string | null>(() => {
		if (selectedAvatarId) return selectedAvatarId
		if (defaultImageUrl) return defaultImageUrl
		if (initials) return createInitialsPreview(initials)
		return null
	})
	const [isDragOver, setIsDragOver] = useState(false)
	const isMobile = useIsMobile()
	const avatarRefs = useRef<(HTMLDivElement | null)[]>([])

	const urlToFile = async (url: string, filename: string): Promise<File> => {
		const response = await fetch(url)
		const blob = await response.blob()
		return new File([blob], filename, { type: blob.type })
	}

	useEffect(() => {
		if (selectedAvatarId === null || selectedAvatarId?.length === 0) {
			handleSelection("initials")
		}
	}, [selectedAvatarId])

	const handleSelection = async (selected: string | File | "initials") => {
		try {
			let url: string
			let file: File

			if (selected === "initials") {
				const { url: initialsUrl, file: initialsFile } =
					await createInitialsAvatar(initials)
				url = initialsUrl
				file = initialsFile
			} else if (typeof selected === "string") {
				url = selected
				const filename = "avatar.jpg"
				file = await urlToFile(url, filename)
			} else {
				file = selected
				url = await new Promise<string>((resolve, reject) => {
					const reader = new FileReader()
					reader.onload = () => resolve(reader.result as string)
					reader.onerror = reject
					reader.readAsDataURL(file)
				})
			}
			setSelected(url)
			onSelect?.({ url, file })
		} catch (error) {
			console.error("Error processing image selection:", error)
		}
	}

	const handleFileInputChange = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		const file = event.target.files?.[0]
		if (file) {
			handleSelection(file)
		}
	}

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault()
		e.stopPropagation()
	}

	const handleDragEnter = (e: React.DragEvent) => {
		e.preventDefault()
		e.stopPropagation()
		setIsDragOver(true)
	}

	const handleDragLeave = (e: React.DragEvent) => {
		e.preventDefault()
		e.stopPropagation()
		if (e.currentTarget.contains(e.relatedTarget as Node)) return
		setIsDragOver(false)
	}

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault()
		e.stopPropagation()
		setIsDragOver(false)

		const files = Array.from(e.dataTransfer.files)
		const imageFile = files.find((file) => file.type.startsWith("image/"))

		if (imageFile) {
			handleSelection(imageFile)
		}
	}

	const initialsPreviewUrl = useMemo(() => {
		if (!initials) return null
		return createInitialsPreview(initials)
	}, [initials])

	// Calculate total number of avatars
	const totalAvatars = (initials ? 1 : 0) + (avatars?.length || 0)

	// Handler to trigger file input
	const triggerFileInput = () => {
		document.getElementById("fileInput")?.click()
	}

	// Keyboard handler for upload button
	const handleUploadKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault()
			triggerFileInput()
		}
	}

	// Keyboard navigation handler for avatar selection
	const handleAvatarKeyDown = (
		e: React.KeyboardEvent,
		index: number,
		avatarUrl: string | "initials"
	) => {
		const cols = 6

		switch (e.key) {
			case "Enter":
			case " ":
				e.preventDefault()
				handleSelection(avatarUrl)
				break
			case "ArrowRight":
				e.preventDefault()
				if (index < totalAvatars - 1) {
					avatarRefs.current[index + 1]?.focus()
				}
				break
			case "ArrowLeft":
				e.preventDefault()
				if (index > 0) {
					avatarRefs.current[index - 1]?.focus()
				}
				break
			case "ArrowDown":
				e.preventDefault()
				if (index + cols < totalAvatars) {
					avatarRefs.current[index + cols]?.focus()
				}
				break
			case "ArrowUp":
				e.preventDefault()
				if (index - cols >= 0) {
					avatarRefs.current[index - cols]?.focus()
				}
				break
			case "Home":
				e.preventDefault()
				avatarRefs.current[0]?.focus()
				break
			case "End":
				e.preventDefault()
				avatarRefs.current[totalAvatars - 1]?.focus()
				break
		}
	}

	return (
		<Box
			className={styles.container}
			onDragOver={handleDragOver}
			onDragEnter={handleDragEnter}
			onDragLeave={handleDragLeave}
			onDrop={handleDrop}
			style={{
				borderWidth: isDragOver ? "3px" : undefined,
				borderStyle: isDragOver ? "dashed" : undefined
			}}
			h={{ base: "100%", md: 280 }}
			{...props}
		>
			{isDragOver ? (
				<Center h="100%">
					<Text fz="lg" ta="center" fw={600}>
						{dictionary?.dragImageLabel ?? "Drop the image here"}
					</Text>
				</Center>
			) : (
				<Flex
					direction={{ base: "column", xs: "row" }}
					gap="md"
					justify="space-between"
					align="center"
					w="100%"
					py={isMobile ? 30 : 46}
					px={{ base: 20, md: 45 }}
				>
					<Stack align="center" justify="center" gap="0px">
						{selected ? (
							<Image className={styles.avatar} w={100} h={100} src={selected} />
						) : (
							photoIcon && <AEMSvg svgData={photoIcon} />
						)}
						{selected ? (
							<Anchor
								underline="always"
								fw={600}
								c="var(--text-color-primary)"
								onClick={triggerFileInput}
								onKeyDown={handleUploadKeyDown}
								tabIndex={0}
								my={10}
								role="button"
								aria-label="Change your profile picture"
							>
								{dictionary?.changeYourPictureLabel}
							</Anchor>
						) : (
							<Anchor
								underline="always"
								fw={600}
								c="var(--text-color-primary)"
								onClick={triggerFileInput}
								onKeyDown={handleUploadKeyDown}
								tabIndex={0}
								my={10}
								role="button"
								aria-label="Upload or drop your photo here"
							>
								{dictionary?.uploadLabel || "Upload or drop your photo here"}
							</Anchor>
						)}
						<Text
							size="13px"
							ta="center"
							c="var(--mantine-color-royal-44)"
							maw="271px"
							fw={500}
						>
							{dictionary?.profilePhotoDescription}
						</Text>
					</Stack>

					<Text fw={600}>{dictionary?.orLabel}</Text>

					<Stack align="center" justify="flex-start" gap="16px">
						<Text size="16px" ta="center" fw={600}>
							{dictionary?.selectYourAvatharLabel}
						</Text>
						<Stack gap={8} align="center">
							<SimpleGrid
								cols={6}
								role="radiogroup"
								aria-label="Avatar selection"
							>
								{initials && (
									<Box
										ref={(el) => {
											avatarRefs.current[0] = el
										}}
										className={styles.avatarWrapper}
										tabIndex={0}
										role="radio"
										aria-checked={selected === initialsPreviewUrl}
										aria-label={`Initials avatar: ${initials}`}
										onClick={() => handleSelection("initials")}
										onKeyDown={(e) => handleAvatarKeyDown(e, 0, "initials")}
									>
										<Image
											className={styles.avatar}
											w={40}
											h={40}
											src={initialsPreviewUrl || ""}
											alt={`Initials avatar: ${initials}`}
										/>
									</Box>
								)}
								{avatars &&
									avatars.length > 0 &&
									avatars.map((avatar, idx) => {
										const avatarIndex = (initials ? 1 : 0) + idx
										return (
											<Box
												key={avatar.fileName}
												ref={(el) => {
													avatarRefs.current[avatarIndex] = el
												}}
												className={styles.avatarWrapper}
												tabIndex={0}
												role="radio"
												aria-checked={selected === avatar.dynamicMediaUrl}
												aria-label={`Avatar ${avatarIndex + 1}`}
												onClick={() => handleSelection(avatar.dynamicMediaUrl)}
												onKeyDown={(e) =>
													handleAvatarKeyDown(
														e,
														avatarIndex,
														avatar.dynamicMediaUrl
													)
												}
											>
												<Image
													className={styles.avatar}
													w={40}
													h={40}
													src={avatar.dynamicMediaUrl}
													alt={`Avatar option ${avatarIndex + 1}`}
												/>
											</Box>
										)
									})}
							</SimpleGrid>
						</Stack>
					</Stack>
				</Flex>
			)}
			{/* Hidden file input */}
			<input
				id="fileInput"
				type="file"
				accept="image/*"
				onChange={handleFileInputChange}
				style={{ display: "none" }}
			/>
		</Box>
	)
}
